/**
 * TotalBudgetCard Component
 *
 * 전체 예산 대비 지출 현황을 표시하는 카드 컴포넌트
 */

/**
 * TotalBudgetCard Props
 */
export interface TotalBudgetCardProps {
  /** 전체 예산 합계 */
  totalBudget: number
  /** 전체 지출 합계 */
  totalSpent: number
  /** 전체 사용 비율 (%) */
  totalPercentage: number
}

/**
 * 전체 예산 현황 카드
 *
 * 책임:
 * - 전체 예산, 지출, 남은 금액 표시
 * - 사용 비율에 따른 프로그레스 바 (색상 코딩)
 * - 시각적으로 눈에 띄는 요약 정보 제공
 *
 * 색상 규칙:
 * - < 60%: 초록색 (안전)
 * - 60-90%: 주황색 (주의)
 * - > 90%: 빨간색 (위험)
 */
export function TotalBudgetCard({ totalBudget, totalSpent, totalPercentage }: TotalBudgetCardProps) {
  /**
   * 사용 비율에 따른 색상 결정
   */
  const getColor = (percentage: number) => {
    if (percentage < 60) return 'green'
    if (percentage < 90) return 'orange'
    return 'red'
  }

  const color = getColor(totalPercentage)

  /**
   * Tailwind CSS 색상 클래스 매핑
   * bg: 프로그레스 바 배경 (테마 색상 사용)
   * text, lightBg: 텍스트/배경 색상 (Tailwind 기본 색상 유지)
   */
  const colorClasses = {
    green: {
      bg: 'bg-budget-safe',
      text: 'text-green-700 dark:text-green-300',
      lightBg: 'bg-green-50 dark:bg-green-900/20',
    },
    orange: {
      bg: 'bg-budget-warning',
      text: 'text-orange-700 dark:text-orange-300',
      lightBg: 'bg-orange-50 dark:bg-orange-900/20',
    },
    red: {
      bg: 'bg-budget-danger',
      text: 'text-red-700 dark:text-red-300',
      lightBg: 'bg-red-50 dark:bg-red-900/20',
    },
  }

  const currentColor = colorClasses[color]

  /**
   * 숫자를 천단위 구분자로 포맷
   */
  const formatCurrency = (amount: number): string => {
    return amount.toLocaleString('ko-KR')
  }

  const remaining = totalBudget - totalSpent

  return (
    <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-4">
      {/* Header */}
      <div className="mb-3">
        <h2 className="text-lg font-bold text-gray-900 dark:text-gray-100 mb-1">전체 예산 현황</h2>
        <p className="text-sm text-gray-500 dark:text-gray-400">이번 달 예산 사용 현황입니다</p>
      </div>

      {/* Amount Display */}
      <div className="mb-3">
        <div className="flex items-baseline justify-between mb-2">
          <div>
            <span className="text-3xl font-bold text-gray-900 dark:text-gray-100">
              {formatCurrency(totalSpent)}
            </span>
            <span className="text-lg text-gray-500 dark:text-gray-400 ml-2">원</span>
          </div>
          <div className="text-right">
            <span className={`text-2xl font-bold ${currentColor.text}`}>
              {totalPercentage.toFixed(1)}%
            </span>
          </div>
        </div>

        <div className="text-sm text-gray-600 dark:text-gray-400">
          전체 예산: {formatCurrency(totalBudget)}원
        </div>
      </div>

      {/* Progress Bar */}
      <div className="mb-3">
        <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-3 overflow-hidden">
          <div
            className={`${currentColor.bg} h-3 rounded-full transition-all duration-500 ease-out`}
            style={{ width: `${Math.min(totalPercentage, 100)}%` }}
          />
        </div>
      </div>

      {/* Remaining Amount */}
      <div className={`${currentColor.lightBg} rounded-lg p-4`}>
        <div className="flex items-center justify-between">
          <span className="text-sm font-medium text-gray-700 dark:text-gray-200">남은 예산</span>
          <span className={`text-lg font-bold ${currentColor.text}`}>
            {remaining >= 0 ? (
              <>{formatCurrency(remaining)}원</>
            ) : (
              <>초과 {formatCurrency(Math.abs(remaining))}원</>
            )}
          </span>
        </div>
      </div>
    </div>
  )
}
